import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from "axios";
import { getAuthToken, refreshIdToken } from "../utils";
import { handleError } from "../utils/errorHandler";
import { addRefreshSubscriber, onRefreshed } from "../services/subscriber";
import * as Sentry from "@sentry/react";
import { setToken } from "./token";
import { handleLogout } from "../components/profileDropdown";
import { showToast } from "./toastUtils";

let isRefreshing = false;

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

export const setupAuthInterceptor = (service: AxiosInstance) => {
  // Request Interceptor
  service.interceptors.request.use(
    async (config) => {
      const token = await getAuthToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => {
      Sentry.captureException(error);
      handleError(error, "Request Interceptor");
      return Promise.reject(error);
    },
  );

  // Response Interceptor
  service.interceptors.response.use(
    (response) => {
      handleSubscriptionError(response.headers);
      return response;
    },
    async (error) => {
      if (axios.isAxiosError(error)) {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        if (!error.response) {
          showToast(
            "Network error: Please check your internet connection.",
            "error",
          );
          // Optionally, you can log the error locally or handle it further
          return Promise.reject(error);
        }

        if (error.response) {
          const { status, data } = error.response;
          if (status !== 0) {
            Sentry.withScope((scope) => {
              scope.setTag("http_status", status);
              scope.setExtra("response_data", data);
              scope.setExtra("request_url", error.config?.url);
              scope.setExtra("request_method", error.config?.method);
              scope.setExtra("request_payload", error.config?.data);
              Sentry.captureException(error);
            });
          }
          if (status === 401 && !originalRequest._retry) {
            if (!isRefreshing) {
              isRefreshing = true;
              try {
                const newToken = await refreshIdToken();
                isRefreshing = false;
                if (newToken) {
                  setToken(newToken); // Update token in localStorage
                  onRefreshed(newToken);
                  originalRequest._retry = true;
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers["Authorization"] =
                    `Bearer ${newToken}`;
                  return service(originalRequest);
                }
              } catch (err) {
                isRefreshing = false;
                handleLogout();
                handleError(err, "Token Refresh");
                return Promise.reject(err);
              }
            } else {
              return new Promise((resolve) => {
                addRefreshSubscriber((newToken: string) => {
                  originalRequest._retry = true;
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers["Authorization"] =
                    `Bearer ${newToken}`;
                  resolve(service(originalRequest));
                });
              });
            }
          } else if (status === 500) {
            handleError(
              `HTTP Error: ${status}`,
              "Axios Response Interceptor 500",
            );
          }
          // Handle other status codes as needed
        } else if (error.request) {
          handleError(error, "Axios Response Interceptor - No Response");
        } else {
          handleError(error, "Axios Response Interceptor - Request Setup");
        }
      } else {
        Sentry.captureException(error);
      }

      return Promise.reject(error);
    },
  );
};

function handleSubscriptionError(headers: any) {
  const activeSubscription = headers["x-active-subscription"] === "false";
  const subscriptionExpired = headers["x-subscription-elapsed"] === "true";
  const usageLimitReached = headers["x-usage-limit-reached"] === "true";

  if (activeSubscription) {
    showToast("No active or processing subscription found", "error");
  }

  if (subscriptionExpired) {
    showToast("Subscription expired", "error");
  }

  if (usageLimitReached) {
    showToast("Subscription limits reached", "info");
  }
}

// function showToast(message: string) {
//   showToast(message, "error")
// }
